import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { BehaviorSubject, interval, Subject, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/notification/notification.service';
import { AuthService } from 'src/app/shared/service/auth.service';
import { routerConfig, RouterService } from 'src/app/shared/service/router.service';
import { TranslationService } from 'src/app/shared/service/translation.service';
import { PasswordValidation } from 'src/app/shared/validators/password-validation.service';
import { environment } from 'src/environments/environment';
import { FormApiValidationError } from '../../shared/model/errors/formApiError.model';
import { SlideInterface } from '../../shared/model/slider.model';
import { passwordStrengthValidator } from '../../shared/helpers/utils';

const SUPPORTED_TYPES: string[] = ['audit', 'beta'];
const SLIDES: SlideInterface[] = [
  { url: 'assets/images/register/visibility-monitor.png', title: 'visibility-monitor' },
  { url: 'assets/images/register/feed-optimiser.png', title: 'feed-optimiser' },
  { url: 'assets/images/register/campaign-generator.png', title: 'campaign-generator' },
  { url: 'assets/images/register/price-monitor.png', title: 'price-monitor' },
];

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent implements OnInit, OnDestroy {
  accountType: string = ''; // @TODO: enum
  isLoading = false;
  isPasswordVisible = false;
  isPasswordConfirmationVisible = false;
  currentSlide = 0;
  hoverPaused = new BehaviorSubject<boolean>(false);
  protected slides: SlideInterface[] = SLIDES;

  protected readonly Array = Array;
  private destroy$: Subject<void> = new Subject();
  private authService = inject(AuthService);
  private formBuilder = inject(FormBuilder);
  private gtmService = inject(GoogleTagManagerService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private routerService = inject(RouterService);
  private translationService = inject(TranslationService);
  privacyPolicyUrl: string = this.translationService.commonUrls().privacyPolicy;
  termsAndconditionsUrl: string = this.translationService.commonUrls().termsAndconditions;
  protected readonly Object = Object;

  readonly form = this.formBuilder.group(
    {
      email: ['', [Validators.email, Validators.required, Validators.minLength(5)]],
      password: ['', [Validators.required, passwordStrengthValidator]],
      password_confirmation: ['', Validators.required],
      regs: [false, Validators.requiredTrue],
      recaptcha: ['', [Validators.required]],
      allow_brand_use: [false],
    },
    {
      validator: PasswordValidation.matchPassword,
    },
  );

  get email() {
    return this.form.get('email') as FormControl;
  }

  get password() {
    return this.form.get('password') as FormControl;
  }

  get passwordConfirmation() {
    return this.form.get('password_confirmation') as FormControl;
  }

  get regs() {
    return this.form.get('regs') as FormControl;
  }

  get recaptchaCtrl() {
    return this.form.get('recaptcha') as FormControl;
  }

  get isCaptchaInvalid() {
    const { dirty, invalid, touched } = this.recaptchaCtrl;
    return invalid && (dirty || touched);
  }

  isObject(value: unknown): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  ngOnInit() {
    const accountType = this.route.snapshot.queryParamMap.get('type');

    if (accountType && SUPPORTED_TYPES.includes(accountType)) {
      this.accountType = accountType;
    }

    this.startAutoSlideChange();
  }

  markRegsDirty(): void {
    this.regs.markAsDirty();
    this.regs.markAsTouched();
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('invalid_form');
      return;
    }

    this.isLoading = true;
    const { email, password, passwordConfirmation, regs } = this;
    this.authService
      .register(
        email.value,
        password.value,
        passwordConfirmation.value,
        regs.value,
        this.accountType,
        this.translationService.getCurrentLanguage(),
      )
      .pipe(
        catchError((er: FormApiValidationError) => this.handleErrorOnRegisterRequest(er)),
        tap(() => this.sendDataForGTM()),
        switchMap(() => this.authService.login(email.value, password.value)),
      )
      .subscribe(() => {
        this.notificationService.success('register_account');
        this.routerService.navigate(routerConfig.home);
      });
  }

  captchaErrored(): void {
    this.notificationService.error('captcha');
  }

  changeSlide(direction: 'next' | 'prev') {
    if (direction === 'next') {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    } else {
      this.currentSlide = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    }
  }

  changeSlideWithKeys(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      this.changeSlide('next');
    } else if (event.key === 'ArrowLeft') {
      this.changeSlide('prev');
    }
  }

  onHoverStart() {
    this.hoverPaused.next(true);
  }

  onHoverEnd() {
    this.hoverPaused.next(false);
  }

  startAutoSlideChange() {
    interval(5000)
      .pipe(
        switchMap(() => this.hoverPaused),
        tap((paused) => {
          if (!paused) {
            this.changeSlide('next');
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private sendDataForGTM() {
    if (!environment.production) {
      return;
    }
    const gtmTag = {
      event: 'sign_up',
    };
    this.gtmService.pushTag(gtmTag);
  }

  private handleErrorOnRegisterRequest(er: FormApiValidationError) {
    this.isLoading = false;
    if (er.setOnForm) {
      er.setOnForm(this.form);
      er.notify();
    }
    return throwError(er);
  }
}
